// Account-bound "saved calendar filters" — named snapshots of the calendar's tenant +
// doctor (+ standalone calendar) selection plus the day-view grouping (the main filter
// line in the calendar header). Staff save a selection once and recall it from the bar
// under the toolbar, or with the number keys 1–9. Up to 9 are kept; they can be freely
// reordered (drag-and-drop), renamed, and deleted.
//
// Persisted per user via the server settings sync core (key "savedCalendarFilters",
// value { items: [...] }) so presets follow the user across devices — the pre-migration
// device-global localStorage list is gone (deliberately not seeded; accounts start empty).

import {
  USER_SETTING_KEYS,
  getActiveSettingsUid,
  readUserSetting,
  subscribeUserSettingsActivation,
  writeUserSetting,
} from "@/lib/stores/user-settings-sync";

const SAVED_FILTERS_CHANGE_EVENT = "healo-saved-calendar-filters-change";

// At most nine presets, so each maps to a single digit key (1–9).
export const MAX_SAVED_CALENDAR_FILTERS = 9;
const MAX_FILTER_NAME_LENGTH = 40;

// The day-view "Group" toggle captured alongside the selection. Mirrors the valid values
// (and default) of the user-preference store's `calendarDayViewGroupBy` so an older preset
// missing the field falls back to the app default rather than an invalid mode.
const VALID_GROUP_BY = ["tenant", "doctor"];
const DEFAULT_GROUP_BY = "tenant";

// { uid, filters } — rebuilt whenever the signed-in identity (or its server blob) changes.
let cache = null;
let activationSubscribed = false;

function normalizeIdArray(value) {
  if (!Array.isArray(value)) {
    return [];
  }
  return uniqueTrimmedIds(value);
}

// The Calendars facet arrived after presets already existed: a preset without a
// `calendarIds` array is a legacy one and keeps meaning "all standalone calendars"
// (null), while a saved array — even an empty one — is applied verbatim.
function normalizeOptionalIdArray(value) {
  if (!Array.isArray(value)) {
    return null;
  }
  return uniqueTrimmedIds(value);
}

function uniqueTrimmedIds(value) {
  const uniqueIds = [];
  const seen = new Set();
  value.forEach((item) => {
    const id = String(item || "").trim();
    if (!id || seen.has(id)) {
      return;
    }
    seen.add(id);
    uniqueIds.push(id);
  });
  return uniqueIds;
}

function normalizeName(value, fallback) {
  const trimmed = typeof value === "string" ? value.trim() : "";
  const name = trimmed || fallback || "Filter";
  return name.slice(0, MAX_FILTER_NAME_LENGTH);
}

function normalizeGroupBy(value) {
  const groupBy = typeof value === "string" ? value.trim().toLowerCase() : "";
  return VALID_GROUP_BY.includes(groupBy) ? groupBy : DEFAULT_GROUP_BY;
}

function makeId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return `scf_${crypto.randomUUID()}`;
  }
  return `scf_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

function normalizeFilter(value) {
  if (!value || typeof value !== "object") {
    return null;
  }
  const id = String(value.id || "").trim() || makeId();
  return {
    id,
    name: normalizeName(value.name, "Filter"),
    tenantIds: normalizeIdArray(value.tenantIds),
    doctorIds: normalizeIdArray(value.doctorIds),
    calendarIds: normalizeOptionalIdArray(value.calendarIds),
    groupBy: normalizeGroupBy(value.groupBy),
  };
}

/** Exposed for tests: the persisted shape of one preset (null when unusable). */
export function normalizeSavedCalendarFilter(value) {
  return normalizeFilter(value);
}

function normalizeFilterList(value) {
  const source = Array.isArray(value) ? value : [];
  const result = [];
  const seenIds = new Set();
  for (const item of source) {
    const filter = normalizeFilter(item);
    if (!filter) {
      continue;
    }
    if (seenIds.has(filter.id)) {
      filter.id = makeId();
    }
    seenIds.add(filter.id);
    result.push(filter);
    if (result.length >= MAX_SAVED_CALENDAR_FILTERS) {
      break;
    }
  }
  return result;
}

function emitChange() {
  if (typeof window === "undefined") {
    return;
  }
  window.dispatchEvent(new Event(SAVED_FILTERS_CHANGE_EVENT));
}

// Lazy re-reads alone never NOTIFY anyone (the pinned-messages lesson): invalidate + emit
// on every identity swap so the presets bar re-renders with the new user's list.
function ensureActivationSubscription() {
  if (activationSubscribed || typeof window === "undefined") {
    return;
  }
  activationSubscribed = true;
  subscribeUserSettingsActivation(() => {
    cache = null;
    emitChange();
  });
}

function loadFilters() {
  ensureActivationSubscription();
  const uid = getActiveSettingsUid();
  if (cache && cache.uid === uid) {
    return cache.filters;
  }
  const stored = uid ? readUserSetting(USER_SETTING_KEYS.SAVED_CALENDAR_FILTERS) : null;
  cache = { uid, filters: normalizeFilterList(stored?.items) };
  return cache.filters;
}

function persistFilters(nextFilters) {
  const uid = getActiveSettingsUid();
  cache = { uid, filters: nextFilters };
  if (uid) {
    writeUserSetting(USER_SETTING_KEYS.SAVED_CALENDAR_FILTERS, { items: nextFilters });
  }
  emitChange();
  return nextFilters;
}

export function getSavedCalendarFilters() {
  return loadFilters();
}

// Append a new preset from the current selection. No-op (returns the existing list) when the
// nine-slot ceiling is reached — callers should hide/disable the save affordance at the limit.
export function addSavedCalendarFilter({ name, tenantIds, doctorIds, calendarIds, groupBy } = {}) {
  const current = loadFilters();
  if (current.length >= MAX_SAVED_CALENDAR_FILTERS) {
    return current;
  }
  // The explicit calendar selection is stored as an array (possibly empty) so the preset
  // restores exactly what was on screen; only legacy presets carry null ("all").
  const filter = normalizeFilter({
    id: makeId(),
    name,
    tenantIds,
    doctorIds,
    calendarIds: Array.isArray(calendarIds) ? calendarIds : [],
    groupBy,
  });
  return persistFilters([...current, filter]);
}

export function removeSavedCalendarFilter(id) {
  const targetId = String(id || "").trim();
  const current = loadFilters();
  const next = current.filter((filter) => filter.id !== targetId);
  if (next.length === current.length) {
    return current;
  }
  return persistFilters(next);
}

export function renameSavedCalendarFilter(id, name) {
  const targetId = String(id || "").trim();
  const current = loadFilters();
  let changed = false;
  const next = current.map((filter) => {
    if (filter.id !== targetId) {
      return filter;
    }
    const nextName = normalizeName(name, filter.name);
    if (nextName === filter.name) {
      return filter;
    }
    changed = true;
    return { ...filter, name: nextName };
  });
  return changed ? persistFilters(next) : current;
}

// Move the preset `activeId` to the slot currently held by `overId` (dnd-kit drag-end
// semantics — equivalent to @dnd-kit/sortable's arrayMove).
export function reorderSavedCalendarFilters(activeId, overId) {
  const fromId = String(activeId || "").trim();
  const toId = String(overId || "").trim();
  const current = loadFilters();
  if (!fromId || !toId || fromId === toId) {
    return current;
  }
  const fromIndex = current.findIndex((filter) => filter.id === fromId);
  const toIndex = current.findIndex((filter) => filter.id === toId);
  if (fromIndex < 0 || toIndex < 0) {
    return current;
  }
  const next = current.slice();
  const [moved] = next.splice(fromIndex, 1);
  next.splice(toIndex, 0, moved);
  return persistFilters(next);
}

export function subscribeSavedCalendarFilters(listener) {
  if (typeof window === "undefined" || typeof listener !== "function") {
    return () => {};
  }
  const handler = () => listener(getSavedCalendarFilters());
  window.addEventListener(SAVED_FILTERS_CHANGE_EVENT, handler);
  return () => {
    window.removeEventListener(SAVED_FILTERS_CHANGE_EVENT, handler);
  };
}
